import { closeAllModals } from '@mantine/modals';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { HTTP_HEADERS } from 'consts';
import { getBearerTokenStr, setRefreshToken } from 'helpers';
import { getRefreshedToken } from './getRefreshedToken';
import { setToken } from './setToken';

let isRefreshing = false;
let requestsToRetry: ((token: string) => void)[] = [];

/**
 * Get error callback for axios instance
 * If request failed with 401 status and is not set param `dont_retry`, it will try to refresh token and retry request
 * If refresh token is invalid, it will reject promise
 *
 * Param `dont_retry` is used to prevent infinite loop of refreshing token
 * Param `dont_retry` can by set by adding `params: { dontRetry: true }` to request config
 * @example
 * ```tsx
 * apiClient.get('/api/v1/some-endpoint', { params: { dontRetry: true } })
 * ```
 *
 * @param instance
 */
export const getErrorCallback =
  (instance: AxiosInstance) =>
  async (
    error: AxiosError & {
      config: AxiosRequestConfig;
    },
  ) => {
    const { config: request, response } = error;
    const responseData = response?.data as any;

    /**
     * Asseco authentication required. Endpoint api/v2/signatures/asseco/certificates can return 401, but in this case we don't want to refresh token
     */
    const isAssecoCase =
      response?.status === 401 &&
      responseData?.message === 'Asseco authentication required.';

    if (
      response?.status === 401 &&
      !request.params?.dont_retry &&
      !isAssecoCase
    ) {
      const retryRequest = new Promise(resolve => {
        requestsToRetry.push((token: string) => {
          // eslint-disable-next-line no-param-reassign
          instance.defaults.headers.common[HTTP_HEADERS.authorization] =
            getBearerTokenStr(token);

          resolve(instance(request));
        });
      });

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const token = await getRefreshedToken();

          setToken(token);
          setRefreshToken(token);

          requestsToRetry.forEach(requestToRetry => {
            requestToRetry(token);
          });

          isRefreshing = false;
          requestsToRetry = [];
        } catch {
          setToken('null');
          setRefreshToken('null');
          closeAllModals();

          return Promise.reject(error);
        }
      }

      return retryRequest;
    }

    return Promise.reject(error);
  };
